import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import ProductCard from './ProductCard';

const CARD_WIDTH_CLASS =
  'w-[42vw] min-w-[140px] max-w-[200px] flex-none xs:w-[44vw] sm:w-[240px] md:w-[280px] lg:w-[300px]';

const BASE_AUTO_SPEED = 1.1;
const MAX_BOOST = 4;
const DRAG_THRESHOLD = 4;

const buildMarqueeProducts = (products) => {
  if (!products?.length) return [];
  return [...products, ...products];
};

const ProductCarousel = ({
  title,
  products,
  viewAllLink,
  cardVariant = 'carousel',
  showQuickAdd = true,
  embedded = false,
  autoScroll = false,
  compactSection = false,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const engine = useRef({
    boost: 1,
    programmatic: false,
    isTouching: false,
    isDragging: false,
    lastScrollLeft: 0,
    lastScrollTime: 0,
    dragStartX: 0,
    dragStartScrollLeft: 0,
    dragMoved: false,
  });

  const displayProducts = useMemo(
    () => (autoScroll ? buildMarqueeProducts(products) : products),
    [autoScroll, products]
  );

  const loopScroll = useCallback((el) => {
    const half = el.scrollWidth / 2;
    if (half <= 0) return;
    if (el.scrollLeft >= half) {
      engine.current.programmatic = true;
      el.scrollLeft -= half;
    }
  }, []);

  useEffect(() => {
    if (!autoScroll || !products?.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const el = scrollRef.current;
    if (!el) return;

    engine.current.lastScrollLeft = el.scrollLeft;
    engine.current.lastScrollTime = performance.now();

    let rafId = 0;
    const tick = () => {
      const node = scrollRef.current;
      const state = engine.current;

      if (node && !state.isTouching && !state.isDragging) {
        state.programmatic = true;
        node.scrollLeft += BASE_AUTO_SPEED * state.boost;
        loopScroll(node);

        if (state.boost > 1.01) {
          state.boost = Math.max(1, state.boost * 0.992);
        } else if (state.boost < 0.99) {
          state.boost = Math.min(1, state.boost * 0.992 + 0.008);
        } else {
          state.boost = 1;
        }
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [autoScroll, products, loopScroll]);

  const recordUserVelocity = useCallback((el) => {
    const state = engine.current;
    const now = performance.now();
    const delta = el.scrollLeft - state.lastScrollLeft;
    const dt = now - state.lastScrollTime;

    if (dt > 0 && dt < 180) {
      const velocity = delta / dt;
      state.boost = Math.min(MAX_BOOST, Math.max(0.6, 1 + Math.abs(velocity) * 12));
    }

    state.lastScrollLeft = el.scrollLeft;
    state.lastScrollTime = now;
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const state = engine.current;
    if (state.programmatic) {
      state.programmatic = false;
      state.lastScrollLeft = el.scrollLeft;
      state.lastScrollTime = performance.now();
      return;
    }

    recordUserVelocity(el);
    loopScroll(el);
  }, [loopScroll, recordUserVelocity]);

  useEffect(() => {
    if (!autoScroll) return;
    const el = scrollRef.current;
    if (!el) return;

    const state = engine.current;

    const onTouchStart = () => {
      state.lastScrollLeft = el.scrollLeft;
      state.lastScrollTime = performance.now();
    };

    const onTouchMove = () => {
      state.isTouching = true;
    };

    const onTouchEnd = () => {
      state.isTouching = false;
      state.lastScrollLeft = el.scrollLeft;
      state.lastScrollTime = performance.now();
    };

    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      state.dragMoved = false;
      state.dragStartX = e.pageX;
      state.dragStartScrollLeft = el.scrollLeft;
      state.lastScrollLeft = el.scrollLeft;
      state.lastScrollTime = performance.now();
    };

    const onMouseMove = (e) => {
      if (e.buttons !== 1) return;
      const deltaX = e.pageX - state.dragStartX;
      if (!state.isDragging && Math.abs(deltaX) < DRAG_THRESHOLD) return;

      state.isDragging = true;
      state.dragMoved = true;
      state.programmatic = true;
      el.scrollLeft = state.dragStartScrollLeft - deltaX;
      loopScroll(el);
      recordUserVelocity(el);
    };

    const onMouseUp = () => {
      if (state.isDragging) {
        state.lastScrollLeft = el.scrollLeft;
        state.lastScrollTime = performance.now();
      }
      state.isDragging = false;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [autoScroll, loopScroll, recordUserVelocity]);

  const showHeader = !embedded && (title || viewAllLink);

  const renderProduct = (product, index) => (
    <div key={`${product.id}-${index}`} className={CARD_WIDTH_CLASS}>
      <ProductCard product={product} variant={cardVariant} showQuickAdd={showQuickAdd} />
    </div>
  );

  return (
    <section className={embedded ? 'py-0' : compactSection ? 'py-5 sm:py-7' : 'py-12'}>
      <div
        className={cn(
          'w-full max-w-[1600px] mx-auto',
          embedded ? 'px-0' : 'px-3 sm:px-4 md:px-6 lg:px-8'
        )}
      >
        {showHeader && (
          <div className={cn('flex items-center justify-between', compactSection ? 'mb-4' : 'mb-8')}>
            {title ? (
              <h2 className="text-2xl sm:text-3xl font-semibold">{title}</h2>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-4">
              {viewAllLink && (
                <a
                  href={viewAllLink}
                  className="text-xs font-medium text-blue-600 hover:underline sm:text-sm"
                >
                  {t('common.viewAll')}
                </a>
              )}
            </div>
          </div>
        )}

        <div
          ref={scrollRef}
          className={cn(
            'product-carousel-track flex gap-5 sm:gap-6 overflow-x-auto overscroll-x-contain scrollbar-hide pb-4',
            autoScroll ? 'cursor-grab active:cursor-grabbing' : 'scroll-smooth snap-x snap-mandatory'
          )}
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          onScroll={autoScroll ? handleScroll : undefined}
          aria-label={autoScroll && title ? `${title} — kaydırılabilir ürün listesi` : undefined}
        >
          {(autoScroll ? displayProducts : products).map(renderProduct)}
        </div>
      </div>
    </section>
  );
};

export default ProductCarousel;
